import { useMemo } from 'react';
import type { StripeDailyBalance, StripePayout, StripeRefund, StripeTransaction } from '@/lib/stripeService';
import AreaChart from '@/components/charts/AreaChart';
import StackedBarChart from '@/components/charts/StackedBarChart';
import HeatmapChart from '@/components/charts/HeatmapChart';
import GaugeChart from '@/components/charts/GaugeChart';
import DonutChart from '@/components/charts/DonutChart';
import { Activity, Banknote, ArrowDownRight, Clock, Layers, Grid3x3, Percent, ChartPie as PieChart } from 'lucide-react';

interface RevenueVsFeesProps {
  dailyBalances: StripeDailyBalance[];
}

export function RevenueVsFeesSection({ dailyBalances }: RevenueVsFeesProps) {
  const chartData = useMemo(() => {
    return dailyBalances.map(d => ({
      label: d.date.slice(8),
      value: d.gross,
      value2: d.fees,
    }));
  }, [dailyBalances]);

  const totalGross = dailyBalances.reduce((s, d) => s + d.gross, 0);
  const totalFees = dailyBalances.reduce((s, d) => s + d.fees, 0);
  const feeRate = totalGross > 0 ? (totalFees / totalGross) * 100 : 0;

  return (
    <div className="glass-panel rounded-2xl p-5">
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-2.5">
          <div className="p-1.5 rounded-xl bg-teal-500/10">
            <Activity size={16} className="text-teal-600" />
          </div>
          <h2 className="text-sm font-bold text-white">Revenue vs Processing Fees</h2>
        </div>
        <div className="flex items-center gap-4 text-right">
          <div>
            <div className="text-[10px] text-white">Gross</div>
            <div className="text-xs font-bold text-white">${(totalGross / 100).toFixed(2)}</div>
          </div>
          <div>
            <div className="text-[10px] text-white">Fees</div>
            <div className="text-xs font-bold text-red-500">${(totalFees / 100).toFixed(2)}</div>
          </div>
          <div>
            <div className="text-[10px] text-white">Fee Rate</div>
            <div className="text-xs font-bold text-amber-600">{feeRate.toFixed(2)}%</div>
          </div>
        </div>
      </div>
      <p className="text-[11px] text-white mb-4 ml-9">
        Daily comparison of gross revenue against Stripe processing fees. Dashed line shows fee deductions.
      </p>
      <AreaChart data={chartData} color="#0d9488" color2="#ef4444" label1="Gross Revenue" label2="Processing Fees" />
    </div>
  );
}

interface DailyStackedProps {
  dailyBalances: StripeDailyBalance[];
}

export function DailyRevenueStackedSection({ dailyBalances }: DailyStackedProps) {
  const chartData = useMemo(() => {
    return dailyBalances.map(d => ({
      label: d.date.slice(8),
      segments: [
        { label: 'Net', value: d.net, color: '#0d9488' },
        { label: 'Fees', value: d.fees, color: '#f59e0b' },
        ...(d.refunds > 0 ? [{ label: 'Refund', value: d.refunds, color: '#ef4444' }] : []),
      ],
    }));
  }, [dailyBalances]);

  return (
    <div className="glass-panel rounded-2xl p-5">
      <div className="flex items-center gap-2.5 mb-1">
        <div className="p-1.5 rounded-xl bg-sky-500/10">
          <Layers size={16} className="text-sky-600" />
        </div>
        <h2 className="text-sm font-bold text-white">Daily Revenue Composition</h2>
      </div>
      <p className="text-[11px] text-white mb-4 ml-9">
        Stacked breakdown showing net revenue, processing fees, and refunds per day.
      </p>
      <StackedBarChart data={chartData} />
      <div className="flex items-center gap-4 mt-3 ml-2 text-[10px] text-white">
        <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded bg-teal-500/100 inline-block" /> Net Revenue</span>
        <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded bg-amber-500/100 inline-block" /> Fees</span>
        <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded bg-red-500/100 inline-block" /> Refunds</span>
      </div>
    </div>
  );
}

interface TransactionHeatmapProps {
  dailyBalances: StripeDailyBalance[];
  year: number;
  month: number;
}

export function TransactionHeatmapSection({ dailyBalances, year, month }: TransactionHeatmapProps) {
  const heatmapData = useMemo(() => {
    const daysInMonth = new Date(year, month, 0).getDate();
    const result: { day: number; weekday: number; value: number }[] = [];
    for (let d = 1; d <= daysInMonth; d++) {
      const date = new Date(year, month - 1, d);
      const weekday = (date.getDay() + 6) % 7;
      const dateStr = `${year}-${String(month).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
      const entry = dailyBalances.find(b => b.date === dateStr);
      result.push({ day: d, weekday, value: entry?.count || 0 });
    }
    return result;
  }, [dailyBalances, year, month]);

  const totalTxns = heatmapData.reduce((s, d) => s + d.value, 0);
  const activeDays = heatmapData.filter(d => d.value > 0).length;
  const busiestDay = heatmapData.reduce((max, d) => d.value > max.value ? d : max, { day: 0, weekday: 0, value: 0 });

  return (
    <div className="glass-panel rounded-2xl p-5">
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-2.5">
          <div className="p-1.5 rounded-xl bg-emerald-500/10">
            <Grid3x3 size={16} className="text-emerald-800" />
          </div>
          <h2 className="text-sm font-bold text-white">Transaction Heatmap</h2>
        </div>
        <div className="flex items-center gap-4 text-right">
          <div>
            <div className="text-[10px] text-white">Total</div>
            <div className="text-xs font-bold text-white">{totalTxns}</div>
          </div>
          <div>
            <div className="text-[10px] text-white">Active Days</div>
            <div className="text-xs font-bold text-emerald-800">{activeDays}</div>
          </div>
          <div>
            <div className="text-[10px] text-white">Peak</div>
            <div className="text-xs font-bold text-teal-600">Day {busiestDay.day}</div>
          </div>
        </div>
      </div>
      <p className="text-[11px] text-white mb-4 ml-9">
        Calendar view of transaction volume. Darker cells indicate higher activity days.
      </p>
      <HeatmapChart data={heatmapData} />
    </div>
  );
}

interface PerformanceGaugesProps {
  totalGrossUSD: number;
  totalFeesUSD: number;
  totalRefundsUSD: number;
  profitMargin: number;
  transactionCount: number;
  activeDays: number;
  daysInMonth: number;
}

export function PerformanceGaugesSection({
  totalGrossUSD, totalFeesUSD, totalRefundsUSD, profitMargin, transactionCount, activeDays, daysInMonth,
}: PerformanceGaugesProps) {
  const feeRate = totalGrossUSD > 0 ? (totalFeesUSD / totalGrossUSD) * 100 : 0;
  const refundRate = totalGrossUSD > 0 ? (totalRefundsUSD / totalGrossUSD) * 100 : 0;
  const activityRate = daysInMonth > 0 ? (activeDays / daysInMonth) * 100 : 0;
  const avgTxnPerDay = activeDays > 0 ? transactionCount / activeDays : 0;

  return (
    <div className="glass-panel rounded-2xl p-5">
      <div className="flex items-center gap-2.5 mb-1">
        <div className="p-1.5 rounded-xl bg-amber-500/10">
          <Percent size={16} className="text-amber-600" />
        </div>
        <h2 className="text-sm font-bold text-white">Performance Metrics</h2>
      </div>
      <p className="text-[11px] text-white mb-5 ml-9">
        Key performance indicators for the period. Green is good, amber needs attention, red is concerning.
      </p>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <GaugeChart
          value={profitMargin}
          max={100}
          label="Profit Margin"
          color={profitMargin >= 30 ? '#10b981' : profitMargin >= 15 ? '#f59e0b' : '#ef4444'}
        />
        <GaugeChart
          value={feeRate}
          max={10}
          label="Fee Rate"
          color={feeRate <= 3 ? '#10b981' : feeRate <= 5 ? '#f59e0b' : '#ef4444'}
        />
        <GaugeChart
          value={refundRate}
          max={10}
          label="Refund Rate"
          color={refundRate <= 1 ? '#10b981' : refundRate <= 3 ? '#f59e0b' : '#ef4444'}
        />
        <GaugeChart
          value={activityRate}
          max={100}
          label="Active Days"
          color={activityRate >= 70 ? '#10b981' : activityRate >= 40 ? '#f59e0b' : '#ef4444'}
        />
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4">
        <div className="text-center p-2.5 bg-white/5 rounded-xl">
          <div className="text-lg font-bold text-white">{avgTxnPerDay.toFixed(1)}</div>
          <div className="text-[10px] text-white">Avg Txn/Day</div>
        </div>
        <div className="text-center p-2.5 bg-white/5 rounded-xl">
          <div className="text-lg font-bold text-white">${(totalGrossUSD / Math.max(transactionCount, 1)).toFixed(2)}</div>
          <div className="text-[10px] text-white">Avg Txn Size</div>
        </div>
        <div className="text-center p-2.5 bg-white/5 rounded-xl">
          <div className="text-lg font-bold text-red-500">${totalFeesUSD.toFixed(2)}</div>
          <div className="text-[10px] text-white">Total Fees</div>
        </div>
        <div className="text-center p-2.5 bg-white/5 rounded-xl">
          <div className="text-lg font-bold text-emerald-800">${(totalGrossUSD - totalFeesUSD - totalRefundsUSD).toFixed(2)}</div>
          <div className="text-[10px] text-white">Net After Fees</div>
        </div>
      </div>
    </div>
  );
}

interface PayoutTimelineProps {
  payouts: StripePayout[];
}

export function PayoutTimelineSection({ payouts }: PayoutTimelineProps) {
  if (payouts.length === 0) return null;

  const totalPaid = payouts.filter(p => p.status === 'paid').reduce((s, p) => s + p.amount, 0);
  const totalPending = payouts.filter(p => p.status !== 'paid').reduce((s, p) => s + p.amount, 0);

  return (
    <div className="glass-panel rounded-2xl p-5">
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-2.5">
          <div className="p-1.5 rounded-xl bg-blue-500/10">
            <Banknote size={16} className="text-blue-600" />
          </div>
          <h2 className="text-sm font-bold text-white">Payout Timeline</h2>
        </div>
        <div className="flex items-center gap-3 text-right">
          <div>
            <div className="text-[10px] text-white">Paid Out</div>
            <div className="text-xs font-bold text-emerald-800">${(totalPaid / 100).toFixed(2)}</div>
          </div>
          {totalPending > 0 && (
            <div>
              <div className="text-[10px] text-white">Pending</div>
              <div className="text-xs font-bold text-amber-600">${(totalPending / 100).toFixed(2)}</div>
            </div>
          )}
        </div>
      </div>
      <p className="text-[11px] text-white mb-4 ml-9">
        Bank payouts from Stripe to your account. Shows timing and status of each transfer.
      </p>
      <div className="space-y-2 max-h-[200px] overflow-y-auto">
        {payouts.map((p) => {
          const created = new Date(p.created * 1000);
          const arrival = new Date(p.arrival_date * 1000);
          const isPaid = p.status === 'paid';
          return (
            <div key={p.id} className={`flex items-center justify-between py-2.5 px-3 rounded-xl ${isPaid ? 'bg-emerald-500/10/60' : 'bg-amber-500/10/60'}`}>
              <div className="flex items-center gap-2.5">
                <div className={`w-2 h-2 rounded-full ${isPaid ? 'bg-emerald-500/100' : 'bg-amber-500/100'}`} />
                <div>
                  <div className="text-xs font-medium text-white">${(p.amount / 100).toFixed(2)}</div>
                  <div className="text-[10px] text-white">
                    {created.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                  </div>
                </div>
              </div>
              <div className="text-right">
                <div className={`text-[10px] font-medium ${isPaid ? 'text-emerald-800' : 'text-amber-600'}`}>
                  {isPaid ? 'Paid' : p.status}
                </div>
                <div className="text-[10px] text-white flex items-center gap-1">
                  <Clock size={9} />
                  {arrival.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

interface RefundSummaryProps {
  refunds: StripeRefund[];
  totalGrossUSD: number;
}

export function RefundSummarySection({ refunds, totalGrossUSD }: RefundSummaryProps) {
  const totalRefunded = refunds.reduce((s, r) => s + r.amount, 0) / 100;
  const refundRate = totalGrossUSD > 0 ? (totalRefunded / totalGrossUSD) * 100 : 0;

  const reasonBreakdown = useMemo(() => {
    const map: Record<string, number> = {};
    for (const r of refunds) {
      const reason = r.reason || 'No reason specified';
      map[reason] = (map[reason] || 0) + r.amount / 100;
    }
    return Object.entries(map).map(([label, value]) => ({ label, value }));
  }, [refunds]);

  if (refunds.length === 0) return null;

  return (
    <div className="glass-panel rounded-2xl p-5">
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-2.5">
          <div className="p-1.5 rounded-xl bg-red-500/10">
            <ArrowDownRight size={16} className="text-red-500" />
          </div>
          <h2 className="text-sm font-bold text-white">Refund Analysis</h2>
        </div>
        <div className="flex items-center gap-3 text-right">
          <div>
            <div className="text-[10px] text-white">Count</div>
            <div className="text-xs font-bold text-white">{refunds.length}</div>
          </div>
          <div>
            <div className="text-[10px] text-white">Total</div>
            <div className="text-xs font-bold text-red-500">${totalRefunded.toFixed(2)}</div>
          </div>
          <div>
            <div className="text-[10px] text-white">Rate</div>
            <div className="text-xs font-bold text-amber-600">{refundRate.toFixed(2)}%</div>
          </div>
        </div>
      </div>
      <p className="text-[11px] text-white mb-4 ml-9">
        Refund breakdown by reason. Monitoring refund patterns helps identify potential issues.
      </p>
      {reasonBreakdown.length > 0 && (
        <DonutChart data={reasonBreakdown} size={160} centerLabel="Refunded" centerValue={`$${totalRefunded.toFixed(0)}`} />
      )}
    </div>
  );
}

interface TransactionTypeProps {
  transactions: StripeTransaction[];
}

export function TransactionTypeSection({ transactions }: TransactionTypeProps) {
  const typeData = useMemo(() => {
    const map: Record<string, number> = {};
    for (const t of transactions) {
      const cat = t.reporting_category || t.type || 'other';
      map[cat] = (map[cat] || 0) + Math.abs(t.amount) / 100;
    }
    return Object.entries(map)
      .sort(([, a], [, b]) => b - a)
      .map(([label, value]) => ({ label, value }));
  }, [transactions]);

  if (typeData.length === 0) return null;

  const total = typeData.reduce((s, d) => s + d.value, 0);

  return (
    <div className="glass-panel rounded-2xl p-5">
      <div className="flex items-center gap-2.5 mb-1">
        <div className="p-1.5 rounded-xl bg-sky-500/10">
          <PieChart size={16} className="text-sky-600" />
        </div>
        <h2 className="text-sm font-bold text-white">Transaction Categories</h2>
      </div>
      <p className="text-[11px] text-white mb-4 ml-9">
        Breakdown of all Stripe balance transactions by reporting category.
      </p>
      <DonutChart data={typeData} size={180} centerLabel="Total" centerValue={`$${total.toFixed(0)}`} />
    </div>
  );
}

interface CumulativeRevenueProps {
  dailyBalances: StripeDailyBalance[];
}

export function CumulativeRevenueSection({ dailyBalances }: CumulativeRevenueProps) {
  const chartData = useMemo(() => {
    let cumulative = 0;
    return dailyBalances.map(d => {
      cumulative += d.net;
      return { label: d.date.slice(8), value: cumulative };
    });
  }, [dailyBalances]);

  if (chartData.length === 0) return null;

  const finalNet = chartData[chartData.length - 1]?.value || 0;

  return (
    <div className="glass-panel rounded-2xl p-5">
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-2.5">
          <div className="p-1.5 rounded-xl bg-emerald-500/10">
            <Activity size={16} className="text-emerald-800" />
          </div>
          <h2 className="text-sm font-bold text-white">Cumulative Net Revenue</h2>
        </div>
        <div className="text-right">
          <div className="text-[10px] text-white">Month End</div>
          <div className="text-xs font-bold text-emerald-800">${(finalNet / 100).toFixed(2)}</div>
        </div>
      </div>
      <p className="text-[11px] text-white mb-4 ml-9">
        Running total of net revenue over the month. Shows growth trajectory and velocity.
      </p>
      <AreaChart data={chartData} color="#10b981" showArea={true} currency="$" />
    </div>
  );
}
